import type { PluginListenerHandle } from "@capacitor/core";
import type { PushNotificationSchema } from "@capacitor/push-notifications";
import { useEffect } from "react";

const pushNotificationsModule =
	import.meta.env.VITE_CAPACITOR_BUILD === "true"
		? import("@capacitor/push-notifications")
		: null;

function parseDataFromNotification(notification: PushNotificationSchema) {
	// on IOS data looks like this:
	// {
	// 	aps: {
	// 		alert: string | { title: string; body: string };
	// 		data: unknown;
	// 	};
	// };
	// on Android data is just notification.data

	if (typeof notification.data === "object" && "aps" in notification.data) {
		if (typeof notification.data.aps.data === "object") {
			return notification.data.aps.data;
		}
	} else {
		return notification.data;
	}
}

function parseUrlFromNotification(notification: PushNotificationSchema) {
	const data = parseDataFromNotification(notification);
	if (
		data &&
		typeof data === "object" &&
		"url" in data &&
		typeof data.url === "string"
	) {
		return data.url as string;
	}
	return null;
}

export function PushNotificationActionPerformedListener() {
	useEffect(() => {
		if (!pushNotificationsModule) {
			return;
		}

		let receivedNotificationListenerHandle: PluginListenerHandle;
		pushNotificationsModule.then((m) => {
			m.PushNotifications.addListener(
				"pushNotificationActionPerformed",
				({ notification }) => {
					const rawUrl = parseUrlFromNotification(notification);
					if (rawUrl) {
						const structuredUrl = new URL(
							rawUrl,
							// url may include origin or not
							rawUrl.includes("http")
								? undefined
								: "https://crm.villagecaregiving.com",
						);

						window.location.href = `${structuredUrl.pathname}?${structuredUrl.searchParams.toString()}`;
					}
				},
			).then((handle) => {
				receivedNotificationListenerHandle = handle;
			});
		});
		return () => {
			receivedNotificationListenerHandle?.remove();
		};
	}, []);

	return null;
}
